/**
 * REST API クライアント
 */
import { fetchAuthSession } from "@aws-amplify/auth";

// REST API エンドポイント
const API_ENDPOINT = process.env.REACT_APP_API_ENDPOINT || "";
// REST API ベース URL を生成
// TS Backend: /graphql → /api に置換
// Python API: そのまま /api を追加
const REST_API_BASE_URL = API_ENDPOINT.endsWith("/graphql")
  ? API_ENDPOINT.replace(/\/graphql$/, "/api")
  : `${API_ENDPOINT}/api`;

/**
 * 認証トークンを取得
 */
async function getAuthToken(): Promise<string | null> {
  try {
    const session = await fetchAuthSession();
    // Cognito User Pool Authorizer requires ID Token (not Access Token)
    return session.tokens?.idToken?.toString() || null;
  } catch (_e) {
    return null;
  }
}

/**
 * REST API リクエストを送信
 * @param {string} path - API パス
 * @param {RequestInit} options - fetch オプション
 * @param {boolean} skipAuth - 認証をスキップするか
 * @return {Promise<T>} API レスポンス
 */
async function request<T>(
  path: string,
  options: RequestInit = {},
  skipAuth = false,
): Promise<T> {
  const token = skipAuth ? null : await getAuthToken();

  const headers: HeadersInit = {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...options.headers,
  };

  const response = await fetch(`${REST_API_BASE_URL}${path}`, {
    ...options,
    headers,
    mode: "cors",
    credentials: "omit",
  });

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    throw new RestApiError(
      errorBody.message || errorBody.detail || `HTTP error ${response.status}`,
      response.status,
      errorBody,
    );
  }

  return response.json();
}

/**
 * REST API エラークラス
 */
export class RestApiError extends Error {
  /**
   * @param {string} message - エラーメッセージ
   * @param {number} status - HTTP ステータスコード
   * @param {unknown} body - レスポンスボディ
   */
  constructor(
    message: string,
    public status: number,
    public body?: unknown,
  ) {
    super(message);
    this.name = "RestApiError";
  }
}

/**
 * REST API クライアント
 * @description GET/POST/PUT/DELETE メソッドを提供
 */
export const restClient = {
  get: <T>(path: string, params?: Record<string, string>) =>
    request<T>(
      params ? `${path}?${new URLSearchParams(params).toString()}` : path,
    ),

  post: <T>(path: string, body?: unknown) =>
    request<T>(path, {
      method: "POST",
      body: body ? JSON.stringify(body) : undefined,
    }),

  put: <T>(path: string, body?: unknown) =>
    request<T>(path, {
      method: "PUT",
      body: body ? JSON.stringify(body) : undefined,
    }),

  delete: <T>(path: string) =>
    request<T>(path, {
      method: "DELETE",
    }),

  /**
   * 認証不要のGETリクエスト
   * @param {string} path - APIパス
   * @param {Record<string, string>} params - クエリパラメータ
   * @return {Promise<T>} レスポンス
   */
  publicGet: <T>(path: string, params?: Record<string, string>) =>
    request<T>(
      params ? `${path}?${new URLSearchParams(params).toString()}` : path,
      {},
      true,
    ),
};

export default restClient;
